import { normalizeHexColor } from "@bso/shared";

/**
 * ── Visual grouping of highlighted verses ─────────────────────────────
 *
 * In published app highlight was NOT a 12% tint with border
 * left: was a **full background block** and contiguous verses of
 * same color formed single continuous block (square corners where they
 * touch). Seen in captures 02 and 07: "2-3" is single lilac card.
 *
 * Pure logic for that layout: decides, for each verse,
 * whether to round top and/or bottom based on IMMEDIATE neighbor colors in
 * list (not numbering: chapter may have gaps).
 */
interface HighlightBlock {
  /** Normalized `#rrggbb` color, or `null` if verse unhighlighted. */
  color: string | null;
  /** Round top corners (previous neighbor does not share color). */
  roundTop: boolean;
  /** Round bottom corners (next neighbor does not share color). */
  roundBottom: boolean;
}

/**
 * @param verses  Verse numbers IN RENDER ORDER.
 * @param colors  Color per verse number (unhighlighted omitted).
 */
export function computeHighlightBlocks(
  verses: readonly number[],
  colors: ReadonlyMap<number, string>,
): Map<number, HighlightBlock> {
  const out = new Map<number, HighlightBlock>();
  const colorAt = (index: number): string | null => {
    const verse = verses[index];
    return verse === undefined ? null : normalizeHexColor(colors.get(verse));
  };

  for (const [index, verse] of verses.entries()) {
    const color = colorAt(index);
    if (color === null) {
      out.set(verse, { color: null, roundTop: true, roundBottom: true });
      continue;
    }
    out.set(verse, {
      color,
      roundTop: colorAt(index - 1) !== color,
      roundBottom: colorAt(index + 1) !== color,
    });
  }

  return out;
}
